import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, TransitionGroup as _TransitionGroup, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "mb-10 flex w-full gap-2 sm:mt-12 sm:mb-20" }
const _hoisted_2 = { class: "mb-10 grid gap-4 sm:grid-cols-3" }
const _hoisted_3 = { class: "col-span-2 flex flex-col gap-2 sm:gap-4" }
const _hoisted_4 = { class: "text-secondary text-lg font-semibold sm:text-2xl" }
const _hoisted_5 = { class: "text-muted-foreground text-xs sm:text-sm" }
const _hoisted_6 = { class: "order-first col-span-1 sm:order-last" }
const _hoisted_7 = ["src"]
const _hoisted_8 = { class: "flex flex-col gap-4" }
const _hoisted_9 = {
  key: "completed",
  class: "flex w-full flex-col items-center gap-5 text-center"
}
const _hoisted_10 = { class: "text-secondary text-2xl font-medium" }
const _hoisted_11 = { class: "text-muted-foreground text-sm" }
const _hoisted_12 = { class: "mt-auto grid grid-cols-2 gap-2 pt-20 sm:grid-cols-3" }
const _hoisted_13 = { class: "flex justify-end gap-2 sm:justify-center" }

import { ArrowRight, Check, HelpCircle, X } from 'lucide-vue-next';
    import { Button } from '@/components/ui/button';
    import { computed, ref, watch } from 'vue';
    import { InputRow } from '@/components/ui/form';
    import { Textarea } from '@/components/ui/textarea';
    import { useForm } from '@inertiajs/vue3';
    import { useI18n } from 'vue-i18n';
    import { usePage } from '@inertiajs/vue3';
    import axios from 'axios';
    import FileUploadZone from '@/components/FileUploadZone.vue';
    import OnboardingStep from './OnboardingStep.vue';
    import type { FileItem, OnboardingStep as OnboardingStepType } from '@/types';
    import type { SharedData, CompanyOnboardingFile } from '@/types';
    import OnboardingHelpVideo from './OnboardingHelpVideo.vue';

    const SKAT_LINK =
        'https://pdcs.skat.dk/dcs-atn-gateway/nemlogin?targetUrl=aHR0cHM6Ly9udHNlLnNrYXQuZGsvbnRzZS1mcm9udC9jb250ZW50P2lkPWZyYW1lOjJGQUF1dG9yaXNlcnJldmlzb3JtZmw=';

    type FormState = OnboardingStepType & {
        status: 'completed' | 'rejected' | 'pending';
        selectedAcceptIndex?: string;
        comment?: string;
        files?: FileItem[];
    };

    
export default /*@__PURE__*/_defineComponent({
  __name: 'OnboardingAssistant',
  props: {
    onboardingSteps: {},
    modelValue: { type: Boolean },
    companyId: {}
  },
  emits: ["update:modelValue", "close", "completed"],
  setup(__props: any, { emit: __emit }) {

    const { t, tm } = useI18n();

    const props = __props;

    const emit = __emit;

    const open = computed({
        get: () => props.modelValue ?? false,
        set: (value) => emit('update:modelValue', value),
    });

    const activeStepIndex = ref(0);

    const formStates = ref<Record<number, FormState>>({});

    const completedSteps = computed(() =>
        props.onboardingSteps.filter((step) => step.is_completed).sort((a, b) => a.order - b.order),
    );

    const incompleteSteps = computed(() =>
        props.onboardingSteps
            .filter((step) => !step.is_completed)
            .sort((a, b) => a.order - b.order),
    );

    const activeStep = computed(() => incompleteSteps.value[activeStepIndex.value] || null);

    const isLastStep = computed(() => activeStepIndex.value === incompleteSteps.value.length - 1);

    const canProceed = computed(() => {
        if (!activeStep.value) return true; // Allow proceeding if there's no active step (all completed)
        const formState = formStates.value[activeStep.value.id];
        return formState?.status === 'completed' || formState?.status === 'rejected';
    });

    const form = useForm({
        steps: [],
    });

    const goToNextStep = () => {
        if (isLastStep.value) {
            form.transform((data) => ({
                ...data,
                steps: Object.entries(formStates.value).map(([id, state]) => ({
                    id: parseInt(id),
                    status: state.status,
                    comment: state.comment,
                    files: state.files,
                })),
            }));
            form.post(route('v2.onboarding.customer.complete', { company: props.companyId }), {
                forceFormData: true,
                onSuccess: () => {
                    emit('completed', incompleteSteps.value.length);
                },
                onError: (error: any) => {},
            });
            return;
        }
        activeStepIndex.value++;
        setFormValues();
    };

    const resetForm = () => {
        formStates.value = {};
        activeStepIndex.value = 0;
    };

    const goToPreviousStep = () => {
        if (activeStepIndex.value > 0) {
            activeStepIndex.value--;
        }
    };

    const close = () => {
        resetForm();
        emit('close');
    };

    // Modify the updateStepStatus function to track which accept button was clicked
    const updateStepStatus = (status: 'completed' | 'rejected', acceptIndex?: string) => {
        if (!activeStep.value) return;

        formStates.value = {
            ...formStates.value,
            [activeStep.value.id]: {
                ...(formStates.value[activeStep.value.id] ?? {}),
                status,
                selectedAcceptIndex: status === 'completed' ? acceptIndex : undefined,
            },
        };
    };

    const setFormValues = () => {
        formStates.value = {
            ...formStates.value,
            [activeStep.value.id]: {
                ...(formStates.value[activeStep.value.id] ?? {}),
                comment: activeStep.value.comment,
                files: activeStep.value.existing_files?.map((file: CompanyOnboardingFile) => ({
                    id: file.id,
                    name: file.original_filename,
                    url: route('v2.onboarding.files.download', {
                        companyOnboardingStep: activeStep.value.id,
                        storageFile: file.id,
                    }),
                    size: 0,
                    uploading: false,
                    uploaded: true,
                    progress: 0,
                    registered: true,
                    mime: file.mime,
                })),
            },
        };
    };

    watch(
        open,
        (newValue) => {
            if (!newValue) return;
            if (activeStep.value && !formStates.value[activeStep.value.id]) {
                setFormValues();
            }
        },
        {
            immediate: true,
        },
    );

    const stepImage = (step: OnboardingStepType) => {
        if (step.service === 'e-conomic') {
            return '/img/onboarding/economic.png';
        }
        if (step.service === 'vat' || step.service === 'e-income') {
            return '/img/onboarding/skat.png';
        }
    };

    const videoLink = (step: OnboardingStepType) => {
        return 'https://cdn.plyr.io/static/demo/View_From_A_Blue_Moon_Trailer-720p.mp4';
    };

    const openLink = (link: string | undefined) => {
        if (!link) return;
        window.open(link, '_blank');
    };

    const handleFileDelete = async ({ id }: { id: number }) => {
        console.log(id);
        await axios.delete(
            route('v2.onboarding.files.delete', {
                companyOnboardingStep: activeStep.value.id,
                storageFile: id,
            }),
        );
    };

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_TransitionGroup, {
        name: "onboarding-step",
        tag: "div",
        class: "flex w-full gap-2"
      }, {
        default: _withCtx(() => [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(incompleteSteps.value, (step) => {
            return (_openBlock(), _createBlock(OnboardingStep, {
              key: step.id,
              step: step,
              "form-state": formStates.value[step.id],
              separator: step !== incompleteSteps.value[incompleteSteps.value.length - 1]
            }, null, 8, ["step", "form-state", "separator"]))
          }), 128)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(completedSteps.value, (step) => {
            return (_openBlock(), _createBlock(OnboardingStep, {
              key: step.id,
              step: step,
              "form-state": formStates.value[step.id],
              separator: step !== completedSteps.value[completedSteps.value.length - 1]
            }, null, 8, ["step", "form-state", "separator"]))
          }), 128))
        ]),
        _: 1
      })
    ]),
    _createVNode(_TransitionGroup, {
      name: "onboarding-content",
      tag: "div",
      class: "relative px-2 sm:px-5"
    }, {
      default: _withCtx(() => [
        (activeStep.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: activeStep.value.id,
              class: "w-full"
            }, [
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  _createElementVNode("h3", _hoisted_4, _toDisplayString(activeStep.value.title), 1),
                  _createElementVNode("p", _hoisted_5, _toDisplayString(activeStep.value.description ??
                            _unref(t)('onboarding.steps.' + activeStep.value.service + '.description')), 1)
                ]),
                _createElementVNode("div", _hoisted_6, [
                  (stepImage(activeStep.value))
                    ? (_openBlock(), _createElementBlock("img", {
                        key: 0,
                        src: stepImage(activeStep.value),
                        alt: "Onboarding Step Image",
                        class: "ml-auto max-w-6 sm:max-w-full"
                      }, null, 8, _hoisted_7))
                    : _createCommentVNode("", true)
                ])
              ]),
              _createElementVNode("div", _hoisted_8, [
                (activeStep.value.allow_comments && formStates.value[activeStep.value.id])
                  ? (_openBlock(), _createBlock(_unref(InputRow), {
                      key: 0,
                      form: _unref(form),
                      name: "comment",
                      label: "onboarding.comment"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(Textarea), {
                          modelValue: formStates.value[activeStep.value.id].comment,
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formStates.value[activeStep.value.id].comment) = $event)),
                          name: "comment",
                          placeholder: _unref(t)('onboarding.comment')
                        }, null, 8, ["modelValue", "placeholder"])
                      ]),
                      _: 1
                    }, 8, ["form"]))
                  : _createCommentVNode("", true),
                (activeStep.value.allow_files && formStates.value[activeStep.value.id])
                  ? (_openBlock(), _createBlock(_unref(InputRow), {
                      key: 1,
                      form: _unref(form),
                      name: "files",
                      label: "onboarding.files"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(FileUploadZone, {
                          modelValue: formStates.value[activeStep.value.id].files,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formStates.value[activeStep.value.id].files) = $event)),
                          multiple: "",
                          "max-uploads": 3,
                          "delete-file": handleFileDelete,
                          "accepts-files": [
                            '.pdf',
                            '.jpg',
                            '.jpeg',
                            '.png',
                            '.doc',
                            '.docx',
                            '.xls',
                            '.xlsx',
                            '.ppt',
                            '.pptx',
                            '.csv',
                        ]
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    }, 8, ["form"]))
                  : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(tm)(
                        'onboarding.steps.' + activeStep.value.service + '.accept',
                    ), (accept, index) => {
                  return (_openBlock(), _createBlock(_unref(Button), {
                    key: index,
                    type: "button",
                    variant: "selector",
                    size: "xl",
                    width: "full",
                    class: _normalizeClass({
                        'border-foreground [&_svg]:text-primary':
                            formStates.value[activeStep.value.id]?.status === 'completed' &&
                            formStates.value[activeStep.value.id]?.selectedAcceptIndex === index,
                    }),
                    onClick: ($event: any) => (updateStepStatus('completed', index))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(Check)),
                      _createTextVNode(" " + _toDisplayString(accept), 1)
                    ]),
                    _: 2
                  }, 1032, ["class", "onClick"]))
                }), 128)),
                _createVNode(_unref(Button), {
                  type: "button",
                  variant: "selector",
                  size: "xl",
                  width: "full",
                  class: _normalizeClass({
                        'border-foreground [&_svg]:text-destructive':
                            formStates.value[activeStep.value.id]?.status === 'rejected',
                    }),
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (updateStepStatus('rejected')))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(X)),
                    _createTextVNode(" " + _toDisplayString(_unref(t)('onboarding.steps.' + activeStep.value.service + '.reject')), 1)
                  ]),
                  _: 1
                }, 8, ["class"]),
                (activeStep.value.service === 'vat')
                  ? (_openBlock(), _createBlock(_unref(Button), {
                      key: 2,
                      type: "button",
                      variant: "selector",
                      size: "xl",
                      width: "full",
                      class: "justify-center border-transparent shadow-none hover:[&_svg]:-rotate-45",
                      onClick: _cache[3] || (_cache[3] = ($event: any) => (openLink(SKAT_LINK)))
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)('onboarding.steps.' + activeStep.value.service + '.give_access')) + " ", 1),
                        _createVNode(_unref(ArrowRight), { class: "transition-transform duration-100" })
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ])
            ]))
          : _createCommentVNode("", true),
        (!incompleteSteps.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
              _createElementVNode("h3", _hoisted_10, _toDisplayString(_unref(t)('onboarding.steps_completed.title')), 1),
              _createElementVNode("p", _hoisted_11, _toDisplayString(_unref(t)('onboarding.assistant.completed')), 1)
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_12, [
      _cache[4] || (_cache[4] = _createElementVNode("div", null, null, -1)),
      _createElementVNode("div", _hoisted_13, [
        (!incompleteSteps.value.length)
          ? (_openBlock(), _createBlock(_unref(Button), {
              key: 0,
              type: "button",
              variant: "outline",
              disabled: _unref(form).processing,
              onClick: close
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('onboarding.close')), 1)
              ]),
              _: 1
            }, 8, ["disabled"]))
          : _createCommentVNode("", true),
        (activeStepIndex.value > 0 && incompleteSteps.value.length)
          ? (_openBlock(), _createBlock(_unref(Button), {
              key: 1,
              type: "button",
              variant: "outline",
              disabled: _unref(form).processing,
              onClick: goToPreviousStep
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('onboarding.back')), 1)
              ]),
              _: 1
            }, 8, ["disabled"]))
          : _createCommentVNode("", true),
        (incompleteSteps.value.length)
          ? (_openBlock(), _createBlock(_unref(Button), {
              key: 2,
              type: "button",
              disabled: !canProceed.value || _unref(form).processing,
              onClick: _withModifiers(goToNextStep, ["prevent"]),
              loading: _unref(form).processing
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(isLastStep.value ? _unref(t)('onboarding.complete') : _unref(t)('onboarding.next')), 1)
              ]),
              _: 1
            }, 8, ["disabled", "loading"]))
          : _createCommentVNode("", true)
      ])
    ])
  ], 64))
}
}

})